import React, { useCallback, useEffect, useState } from 'react'

import { DELIVERY_ISSUE_CHECK_ICON } from '../../constants/urls'
import { useApi } from '../../hooks/useApi'

import Button from '../shared/Button'
import Loader from '../shared/Loader'
import Modal from '../shared/Modal'

interface DeliveryIssueModalProps {
  handleClose: () => void
  issueType: string
  onReportSuccess: () => void
  pinflagId: string
  show: boolean
}

type ModalStatus = 'loading' | 'success' | 'error'

interface DeliveryIssueContent {
  successDescription: string
  successDetail: string
  successTitle: string
}

const DELIVERY_ISSUE_CONTENT: Record<string, DeliveryIssueContent> = {
  not_received: {
    successDescription: 'Registramos que no recibiste tu pedido.',
    successDetail: 'Nuestro equipo lo revisará y te contactará a la brevedad.',
    successTitle: '¡Listo, reporte enviado!'
  }
}

const BUTTON_COLOR = '#000000'

const getModalTitle = (status: ModalStatus, content: DeliveryIssueContent) => {
  if (status === 'success') return content.successTitle
  if (status === 'error') return 'No pudimos enviar el reporte'
  return 'Enviando reporte...'
}

const DeliveryIssueModal: React.FC<DeliveryIssueModalProps> = ({
  handleClose,
  issueType,
  onReportSuccess,
  pinflagId,
  show
}: DeliveryIssueModalProps) => {
  const { reportDeliveryIssue } = useApi()
  const [status, setStatus] = useState<ModalStatus>('loading')
  const content = DELIVERY_ISSUE_CONTENT[issueType] || DELIVERY_ISSUE_CONTENT.not_received

  const handleReport = useCallback(async () => {
    setStatus('loading')
    const result = await reportDeliveryIssue(pinflagId, issueType)
    if (result.error) {
      setStatus('error')
      return
    }
    setStatus('success')
    onReportSuccess()
  }, [issueType, onReportSuccess, pinflagId, reportDeliveryIssue])

  useEffect(() => {
    if (show) handleReport()
  }, [handleReport, show])

  const title = getModalTitle(status, content)
  let description: React.ReactNode
  if (status === 'error') description = 'No pudimos enviar el reporte. Inténtalo nuevamente.'
  if (status === 'success') {
    description = (
      <>
        <span className="block">{content.successDescription}</span>
        <span className="block">{content.successDetail}</span>
      </>
    )
  }

  let modalIcon: React.ReactNode
  if (status === 'success') {
    modalIcon = (
      <img
        src={DELIVERY_ISSUE_CHECK_ICON}
        alt="Reporte enviado"
        className="h-12 w-12"
      />
    )
  }
  if (status === 'loading') modalIcon = <Loader small />

  let action: React.ReactNode
  if (status === 'success') {
    action = <Button text="Salir" companyColor={BUTTON_COLOR} dark onClick={handleClose} />
  }
  if (status === 'error') {
    action = (
      <Button
        text="Intentar nuevamente"
        companyColor={BUTTON_COLOR}
        dark
        onClick={handleReport}
      />
    )
  }

  return (
    <Modal
      show={show}
      icon={modalIcon}
      title={title}
      description={description}
      containerClassName="m-auto relative w-[calc(100%_-_2rem)] max-w-[380px] rounded-3xl border border-gray-200 bg-white px-3 py-6 shadow-lg"
      contentClassName={`flex flex-col items-center justify-center px-4 ${status === 'error' ? 'gap-5' : 'gap-2'}`}
      titleClassName="text-center font-['Onest'] text-xl font-semibold leading-7"
      descriptionClassName={`text-center font-['Geist'] leading-6 text-gray-500 ${status === 'error' ? 'text-sm' : 'text-base'}`}
    >
      {action && <div className={`${status === 'error' ? 'mt-5' : 'mt-8'} h-12`}>{action}</div>}
    </Modal>
  )
}

export default DeliveryIssueModal
